import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import {
  TableTitleWrapper,
  TableBoxesWrapper,
  SearchContainer,
} from '__pages/Account/pages/Profile/Forms/tableStyles';
import ProfileInfiniteTable from 'pages/Account/pages/Profile/Forms/ProfileInfiniteTable';
import BasicModal from '__components/Modals/presets/BasicModal';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import ApiKeysModalBody from 'pages/Account/pages/Profile/Forms/ApiKeysModalBody';
import { PageSearchInput } from 'components/Layout/PageToolbar';
import { useApiKeys } from 'hooks/apiKeys/useApiKeys';
import { createColumns } from 'pages/Account/pages/Profile/Forms/APIKeys/columns';
import { APIKeysNotice } from 'pages/Account/pages/Profile/Forms/APIKeys/styles';
import {
  ScrollContainerBlocker,
  ScrollContainerBlockerWrapper,
} from 'components/Tables/ScrollContainerStyles';
import type { AccessStruct } from '@kizen/permissions/types';

const KEY_WIDTH = '135px';
const TEAM_MEMBER_WIDTH = '220px';
const REFRESH_WIDTH = '170px';
const ACTION_WIDTH = '51px';
const PADDING_WIDTH = '20px';

const columnWidths = {
  key: KEY_WIDTH,
  employee: TEAM_MEMBER_WIDTH,
  created: REFRESH_WIDTH,
  actions: ACTION_WIDTH,
  padding: PADDING_WIDTH,
};

type APIKeysProps = {
  businessId: string;
  access: AccessStruct;
};

export const APIKeys = ({ businessId, access }: APIKeysProps) => {
  const { t } = useTranslation();

  const {
    handleViewApiKey: onKeyView,
    apiKeys: keys,
    createdKey,
    viewedKey,
    viewEmployee,
    messageHeadData,
    messagesSort,
    setMessagesSort,
    apiKeysModalProps,
    apiKeysModalButtonProps,
    search,
    setSearch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    status,
    confirmDeletionModalProps,
    confirmDeletionModal,
    setToDelete,
    anyApiKeys,
  } = useApiKeys(null);

  const columns = useMemo(
    () =>
      createColumns({
        onSelectAction: (
          { value }: { value: string },
          key: { id: string; employee: unknown }
        ) => {
          const { id, employee } = key;
          if (value === 'onKeyView') {
            onKeyView(id, employee);
          }
          if (value === 'onKeyDelete') {
            setToDelete(key);
            (confirmDeletionModal as { show: () => void }).show();
          }
        },
        columnWidths,
        canRemove: access.remove,
        t,
      }),
    [onKeyView, confirmDeletionModal, access, setToDelete, t]
  );

  return (
    <>
      <TableTitleWrapper marginBottom="11">
        <KizenTypography as="h3" type="subheader">
          {t('API Keys')}
        </KizenTypography>
        <SearchContainer>
          <PageSearchInput
            placeholder={t('Find Keys')}
            value={search}
            onChange={setSearch}
            loading={isFetchingNextPage || isFetching}
          />
        </SearchContainer>
      </TableTitleWrapper>
      <TableBoxesWrapper>
        <APIKeysNotice>
          <KizenTypography as="div">
            {t(
              '(For Techies) API Keys allow full access to accounts, simulating all permissions of a logged in user. API Keys are only necessary to connect from other tools.'
            )}
            &nbsp;
            {!anyApiKeys ? t(`You don't yet have any API Keys.`) : false}
          </KizenTypography>
        </APIKeysNotice>
      </TableBoxesWrapper>
      {keys ? (
        <>
          <ScrollContainerBlockerWrapper className="scroll-container-blocker-wrapper">
            <ScrollContainerBlocker />
          </ScrollContainerBlockerWrapper>
          <ProfileInfiniteTable
            columns={columns}
            data={keys}
            messageHeadData={messageHeadData}
            messagesSort={messagesSort}
            setMessagesSort={setMessagesSort}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            isFetching={isFetching}
            status={status}
          />
        </>
      ) : (
        false
      )}
      <BasicModal {...apiKeysModalButtonProps} {...apiKeysModalProps}>
        <ApiKeysModalBody
          apiKey={createdKey || viewedKey}
          userId={(viewEmployee as { user_id?: string }).user_id ?? ''}
          businessId={businessId}
          showCreatedKeyInfo={!!createdKey}
        />
      </BasicModal>
      <ConfirmDeletionModal
        heading={t('Please Confirm Deletion')}
        {...confirmDeletionModalProps}
      >
        <KizenTypography>
          {t(
            'Deleting this API key will make all subsequent requests using it denied by Kizen.'
          )}
        </KizenTypography>
      </ConfirmDeletionModal>
    </>
  );
};
